import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import AddIcon from '@material-ui/icons/Add';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PaletteOutlinedIcon from '@material-ui/icons/PaletteOutlined';
import SearchIcon from '@material-ui/icons/Search';
import StraightenOutlinedIcon from '@material-ui/icons/StraightenOutlined';
import ToggleOffOutlinedIcon from '@material-ui/icons/ToggleOffOutlined';
import ViewListOutlinedIcon from '@material-ui/icons/ViewListOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import DraggableCollectionRow from 'components/kea/DraggableCollectionRow';
import PinAction from 'components/kea/PinAction';
import {
  BOOLEAN_VALUES,
  COLOR_GROUPS,
  NUMBER_REPORT,
  STRING_GROUPS,
  TYPE_OPTIONS,
  UNGROUPED_COLORS,
  UNGROUPED_STRINGS,
  VALUE_ORDER,
} from './fixtureFacetPrototypeData';

const cloneFixture = value => JSON.parse(JSON.stringify(value));
const interpolate = (message, values = {}) => Object.keys(values).reduce(
  (result, key) => result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(values[key])),
  message,
);
const fallbackTranslate = (id, fallback, values) => interpolate(fallback, values);

const createInitialContent = () => ({
  booleanValues: cloneFixture(BOOLEAN_VALUES),
  groups: {
    string: { groups: cloneFixture(STRING_GROUPS), ungrouped: cloneFixture(UNGROUPED_STRINGS) },
    color: { groups: cloneFixture(COLOR_GROUPS), ungrouped: cloneFixture(UNGROUPED_COLORS) },
  },
  displayOrder: { items: cloneFixture(VALUE_ORDER), sort: 'count-desc' },
});

const trapTabInside = (event) => {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(event.currentTarget.querySelectorAll(
    'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
  )).filter(element => element.getClientRects().length > 0);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

export const TypeIcon = ({ type }) => {
  if (type === 'boolean') return <ToggleOffOutlinedIcon />;
  if (type === 'color') return <PaletteOutlinedIcon />;
  if (type === 'number') return <StraightenOutlinedIcon />;
  return <ViewListOutlinedIcon />;
};

TypeIcon.propTypes = { type: PropTypes.string.isRequired };

const CheckBox = ({ checked, onChange, label, ariaLabel }) => (
  <label className="fp-check">
    <input
      type="checkbox"
      checked={checked}
      aria-label={ariaLabel || undefined}
      onChange={event => onChange(event.target.checked)}
    />
    <span aria-hidden="true"><CheckIcon /></span>
    {label && <strong>{label}</strong>}
  </label>
);

CheckBox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
};

CheckBox.defaultProps = { label: null, ariaLabel: '' };

const ValuePickerList = ({ values, selection, onToggle, emptyText, translate: t }) => (
  <div className="fp-value-picker">
    {values.map(value => (
      <CheckBox
        key={value.id}
        checked={selection.includes(value.id)}
        onChange={() => onToggle(value.id)}
        label={<span><strong>{value.label}</strong><small>{value.count.toLocaleString()} {t('facets.policy.board.products', 'products')}</small></span>}
        ariaLabel={t('facets.policy.selectValue', 'Select {label} ({id})', {
          label: value.label,
          id: value.id,
        })}
      />
    ))}
    {!values.length && <div className="fp-complete-state"><CheckIcon /><span>{emptyText}</span></div>}
  </div>
);

ValuePickerList.propTypes = {
  values: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number })).isRequired,
  selection: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  translate: PropTypes.func,
};

ValuePickerList.defaultProps = {
  emptyText: 'No values are available.',
  translate: fallbackTranslate,
};

const ValuePickerDialog = ({ open, title, eyebrow, values, selection, onToggle, onClose, onConfirm, actionLabel, translate: t }) => {
  if (!open) return null;
  return (
    <div className="fp-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="fp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="value-picker-title"
        onMouseDown={event => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Escape') onClose();
          else if (event.key === 'Enter' && event.target.tagName !== 'BUTTON' && selection.length) {
            event.preventDefault();
            onConfirm();
          }
          else trapTabInside(event);
        }}
      >
        <form className="fp-dialog-form" onSubmit={(event) => { event.preventDefault(); if (selection.length) onConfirm(); }}>
          <header>
            <div><span className="fp-eyebrow">{eyebrow}</span><h2 id="value-picker-title">{title}</h2></div>
            <button type="button" className="fp-icon-button" onClick={onClose} aria-label={t('common.close', 'Close')}><CloseIcon /></button>
          </header>
          <ValuePickerList values={values} selection={selection} onToggle={onToggle} translate={t} />
          <footer>
            <button type="button" className="fp-button fp-button--quiet" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
            <button type="submit" className="fp-button fp-button--primary" disabled={!selection.length}>{actionLabel}</button>
          </footer>
        </form>
      </div>
    </div>
  );
};

ValuePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  eyebrow: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number })).isRequired,
  selection: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  actionLabel: PropTypes.string.isRequired,
  translate: PropTypes.func,
};

ValuePickerDialog.defaultProps = { translate: fallbackTranslate };

const Switch = ({ checked, onChange, label, description }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className="fp-switch-row"
    onClick={() => onChange(!checked)}
  >
    <span>
      <strong>{label}</strong>
      <small>{description}</small>
    </span>
    <span className={`fp-switch ${checked ? 'is-on' : ''}`} aria-hidden="true"><span /></span>
  </button>
);

Switch.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export const BasicsEditor = ({ draft, setDraft, onContinue, children, translate: t }) => (
  <div className="fp-editor-section">
    <header className="fp-section-heading">
      <span className="fp-eyebrow">{t('facets.policy.editor.section.metadata', 'Basics')}</span>
      <h2>{t('facets.policy.editor.metadata.heading', 'How shoppers see this filter')}</h2>
    </header>
    <div className="fp-form-card">
      <label className="fp-field">
        <span>{t('facets.policy.editor.field.title', 'Filter name')}</span>
        <input
          value={draft.title}
          onChange={event => setDraft({ ...draft, title: event.target.value })}
          placeholder={t('facets.policy.editor.field.titlePlaceholder', 'Visible in the storefront')}
        />
        {!draft.title.trim() && <small className="is-error">{t('facets.policy.editor.field.titleRequired', 'Enter a name before saving.')}</small>}
      </label>
      <div className="fp-field">
        <span>{t('facets.policy.presentation.eyebrow', 'Presentation')}</span>
        <div className="fp-type-grid">
          {TYPE_OPTIONS.map(option => (
            <button
              type="button"
              key={option.id}
              className={draft.type === option.id ? 'is-active' : ''}
              aria-pressed={draft.type === option.id}
              onClick={() => setDraft({ ...draft, type: option.id, searchable: option.id === 'string' })}
            >
              <TypeIcon type={option.id} />
              <strong>{t(`facets.policy.editor.type.${option.id}.title`, option.title)}</strong>
              <small>{t(`facets.policy.editor.type.${option.id}.description`, option.description)}</small>
              <span className="fp-choice-mark"><CheckIcon /></span>
            </button>
          ))}
        </div>
        <button type="button" className="fp-configure-values" onClick={onContinue}>
          {draft.type === 'number'
            ? t('facets.policy.editor.configureData', 'Configure data')
            : t('facets.policy.editor.configureValues', 'Configure values')} <ArrowForwardIcon />
        </button>
      </div>
    </div>
    <div className="fp-form-card fp-switches-card">
      <Switch
        checked={draft.visible}
        onChange={visible => setDraft({ ...draft, visible })}
        label={t('facets.policy.editor.field.visible', 'Show this filter')}
        description={t('facets.policy.editor.field.visibleDescription', 'Hide it temporarily without deleting its setup.')}
      />
      {draft.type === 'string' && (
        <Switch
          checked={draft.searchable}
          onChange={searchable => setDraft({ ...draft, searchable })}
          label={t('facets.policy.editor.field.searchable', 'Search inside values')}
          description={t('facets.policy.editor.field.searchableDescription', 'Useful when this filter can contain a long list.')}
        />
      )}
    </div>
    {children}
  </div>
);

BasicsEditor.propTypes = {
  draft: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.string,
    visible: PropTypes.bool,
    searchable: PropTypes.bool,
  }).isRequired,
  setDraft: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  children: PropTypes.node,
  translate: PropTypes.func,
};

BasicsEditor.defaultProps = { children: null, translate: fallbackTranslate };

const ValueCard = ({ value, index, translate: t }) => (
  <DraggableCollectionRow draggableId={`mapping-${value.id}`} index={index} className="fp-source-value fp-direct-row">
    <span><strong>{value.label}</strong><small>{value.count.toLocaleString()} {t('facets.policy.board.products', 'products')}</small></span>
  </DraggableCollectionRow>
);

ValueCard.propTypes = {
  value: PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number }).isRequired,
  index: PropTypes.number.isRequired,
  translate: PropTypes.func,
};

ValueCard.defaultProps = { translate: fallbackTranslate };

export const BooleanMapper = ({ values, onChange, translate: t }) => {
  const [pickerLane, setPickerLane] = useState(null);
  const [pickerSelection, setPickerSelection] = useState([]);
  const lanes = ['unassigned', 'yes', 'no'];
  const laneCopy = {
    unassigned: [
      t('facets.policy.boolean.unassigned', 'Needs a decision'),
      t('facets.policy.boolean.unassignedDescription', 'Catalog values not yet shown to shoppers'),
    ],
    yes: [
      t('facets.policy.boolean.yes', 'Yes'),
      t('facets.policy.boolean.yesDescription', 'Values that mean the product is available'),
    ],
    no: [
      t('facets.policy.boolean.no', 'No'),
      t('facets.policy.boolean.noDescription', 'Values that mean the product is unavailable'),
    ],
  };

  const move = (ids, lane) => {
    onChange(values.map(value => (ids.includes(value.id) ? { ...value, lane } : value)));
    setPickerSelection([]);
  };

  const handleDrag = (result) => {
    if (!result.destination) return;
    const valueId = result.draggableId.replace('mapping-', '');
    move([valueId], result.destination.droppableId.replace('boolean-', ''));
  };

  const togglePickerValue = id => setPickerSelection(current => (
    current.includes(id) ? current.filter(candidate => candidate !== id) : [...current, id]
  ));
  const closePicker = () => { setPickerLane(null); setPickerSelection([]); };

  return (
    <div className="fp-mapping-workspace">
      <DragDropContext onDragEnd={handleDrag}>
        <div className="fp-boolean-lanes">
          {lanes.map(lane => {
            const laneValues = values.filter(value => value.lane === lane);
            return (
              <Droppable droppableId={`boolean-${lane}`} key={lane}>
                {(provided, snapshot) => (
                  <section className={`fp-mapping-lane fp-mapping-lane--${lane} ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                    <header>
                      <div><h3>{laneCopy[lane][0]}</h3><p>{laneCopy[lane][1]}</p></div>
                      <span className="fp-lane-actions">
                        {lane !== 'unassigned' && (
                          <button
                            type="button"
                            className="fp-icon-button"
                            aria-label={t('facets.policy.editor.addValuesTo', 'Add values to {label}', { label: laneCopy[lane][0] })}
                            onClick={() => { setPickerLane(lane); setPickerSelection([]); }}
                          ><AddIcon /></button>
                        )}
                        <strong>{laneValues.length}</strong>
                      </span>
                    </header>
                    <div ref={provided.innerRef} {...provided.droppableProps} className="fp-mapping-list">
                      {laneValues.map((value, index) => (
                        <ValueCard key={value.id} value={value} index={index} translate={t} />
                      ))}
                      {provided.placeholder}
                      {!laneValues.length && <span className="fp-drop-empty">{t('facets.policy.editor.dropValuesHere', 'Drop values here')}</span>}
                    </div>
                  </section>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
      <ValuePickerDialog
        open={Boolean(pickerLane)}
        eyebrow={t('facets.policy.editor.catalogValues', 'Catalog values')}
        title={t('facets.policy.editor.addValuesTo', 'Add values to {label}', {
          label: pickerLane === 'yes' ? t('facets.policy.boolean.yes', 'Yes') : t('facets.policy.boolean.no', 'No'),
        })}
        values={values.filter(value => value.lane !== pickerLane)}
        selection={pickerSelection}
        onToggle={togglePickerValue}
        onClose={closePicker}
        onConfirm={() => { move(pickerSelection, pickerLane); closePicker(); }}
        actionLabel={t('facets.policy.editor.addSelected', 'Add selected')}
        translate={t}
      />
    </div>
  );
};

BooleanMapper.propTypes = {
  values: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number, lane: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
  translate: PropTypes.func,
};

BooleanMapper.defaultProps = { translate: fallbackTranslate };

const groupValueId = id => `group-value-${id}`;
const groupDropId = id => `group-target-${id}`;
const sortGroupValues = (values, order) => {
  if (order === 'id') return values;
  return values.map((value, index) => ({ value, index })).sort((left, right) => {
    const comparison = order === 'products'
      ? Number(right.value.count || 0) - Number(left.value.count || 0)
      : String(left.value.label || '').localeCompare(String(right.value.label || ''), undefined, { sensitivity: 'base' });
    if (comparison !== 0) return comparison;
    return left.index - right.index;
  }).map(item => item.value);
};
const normalizeHexColor = color => (/^#[0-9a-f]{6}$/i.test(String(color || ''))
  ? String(color).toUpperCase() : '#8798A5');

const defaultGroupIdFactory = () => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const bytes = new Uint8Array(22);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  return `g_${Array.from(bytes, byte => alphabet[byte % alphabet.length]).join('')}`;
};

const GroupValue = ({ value, index, source, translate: t }) => (
  <DraggableCollectionRow
    draggableId={groupValueId(value.id)}
    index={index}
    className={`fp-group-value fp-direct-row ${source ? 'is-source' : ''}`}
    rowProps={{ 'data-group-value-id': value.id }}
  >
    <span>
      <strong>{value.label}</strong>
      <small>{value.meta ? `${value.meta} · ` : ''}{value.count.toLocaleString()} {t('facets.policy.board.products', 'products')}</small>
    </span>
  </DraggableCollectionRow>
);

GroupValue.propTypes = {
  value: PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, meta: PropTypes.string, count: PropTypes.number }).isRequired,
  index: PropTypes.number.isRequired,
  source: PropTypes.bool,
  translate: PropTypes.func,
};

GroupValue.defaultProps = { source: false, translate: fallbackTranslate };

export const GroupEditor = ({ type, groups, ungrouped, onChange, groupIdFactory, translate: t }) => {
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupDialogDiscard, setGroupDialogDiscard] = useState(false);
  const [draggingGroupValue, setDraggingGroupValue] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#8798a5');
  const [newGroupValues, setNewGroupValues] = useState([]);
  const [valueDialogGroupId, setValueDialogGroupId] = useState(null);
  const [pickerSelection, setPickerSelection] = useState([]);
  const [inlineEditingGroupId, setInlineEditingGroupId] = useState(null);
  const [inlineGroupName, setInlineGroupName] = useState('');
  const [groupPendingDelete, setGroupPendingDelete] = useState(null);
  const [sourceQuery, setSourceQuery] = useState('');
  const [sourceOrder, setSourceOrder] = useState('id');
  const groupDialogBaselineRef = useRef('');
  const keepGroupEditingRef = useRef(null);
  const colorValid = /^#[0-9a-f]{6}$/i.test(newColor);

  const groupDialogState = (editingId, name, color, selectedValues) => JSON.stringify({
    editingId,
    name,
    color: type === 'color' ? color : null,
    selectedValues: [...selectedValues].sort(),
  });

  const closeGroupDialog = () => {
    setGroupDialog(false);
    setGroupDialogDiscard(false);
  };

  const requestGroupDialogClose = () => {
    const currentState = groupDialogState(editingGroupId, newName, newColor, newGroupValues);
    if (currentState !== groupDialogBaselineRef.current) setGroupDialogDiscard(true);
    else closeGroupDialog();
  };

  const moveValues = (ids, destinationId, requestedIndex) => {
    const catalog = new Map([
      ...ungrouped,
      ...groups.reduce((values, group) => [...values, ...group.values], []),
    ].map(value => [value.id, value]));
    const moving = ids.map(id => catalog.get(id)).filter(Boolean);
    if (!moving.length) return;

    const nextUngrouped = ungrouped.filter(value => !ids.includes(value.id));
    const nextGroups = groups.map(group => ({
      ...group,
      values: group.values.filter(value => !ids.includes(value.id)),
    }));
    const destination = destinationId === 'group-source'
      ? nextUngrouped
      : nextGroups.find(group => groupDropId(group.id) === destinationId)?.values;
    if (!destination) return;
    const index = Number.isInteger(requestedIndex)
      ? Math.max(0, Math.min(requestedIndex, destination.length))
      : destination.length;
    destination.splice(index, 0, ...moving);
    onChange({ groups: nextGroups, ungrouped: nextUngrouped });
  };

  const openNewGroup = (selectedValues = []) => {
    setEditingGroupId(null);
    setNewName('');
    setNewColor('#8798a5');
    setNewGroupValues(selectedValues);
    groupDialogBaselineRef.current = groupDialogState(null, '', '#8798a5', selectedValues);
    setGroupDialogDiscard(false);
    setGroupDialog(true);
  };

  const openEditGroup = (group) => {
    setEditingGroupId(group.id);
    setNewName(group.label);
    setNewColor(group.color || '#8798a5');
    const selectedValues = group.values.map(value => value.id);
    setNewGroupValues(selectedValues);
    groupDialogBaselineRef.current = groupDialogState(group.id, group.label, group.color || '#8798a5', selectedValues);
    setGroupDialogDiscard(false);
    setGroupDialog(true);
  };

  const startInlineRename = (group) => {
    setInlineEditingGroupId(group.id);
    setInlineGroupName(group.label);
  };

  const cancelInlineRename = () => {
    setInlineEditingGroupId(null);
    setInlineGroupName('');
  };

  const saveInlineRename = (event) => {
    event.preventDefault();
    const label = inlineGroupName.trim();
    if (!label || !inlineEditingGroupId) return;
    onChange({
      groups: groups.map(group => (group.id === inlineEditingGroupId ? { ...group, label } : group)),
      ungrouped,
    });
    cancelInlineRename();
  };

  const handleDrag = (result) => {
    setDraggingGroupValue(false);
    if (!result.destination) return;
    const id = result.draggableId.replace('group-value-', '');
    if (result.destination.droppableId === 'group-create') {
      openNewGroup([id]);
      return;
    }
    if (result.source.droppableId === 'group-source' && result.destination.droppableId === 'group-source') return;
    moveValues([id], result.destination.droppableId, result.destination.index);
  };

  const saveGroup = () => {
    if (!newName.trim() || (type === 'color' && !colorValid)) return;
    let nextGroups = groups;
    let nextUngrouped = ungrouped;
    if (editingGroupId) {
      const catalog = new Map([
        ...ungrouped,
        ...groups.reduce((values, group) => [...values, ...group.values], []),
      ].map(value => [value.id, value]));
      const selectedValues = newGroupValues.map(id => catalog.get(id)).filter(Boolean);
      const editedGroup = groups.find(group => group.id === editingGroupId);
      const deselectedValues = editedGroup
        ? editedGroup.values.filter(value => !newGroupValues.includes(value.id)) : [];
      nextGroups = groups.map(group => (group.id === editingGroupId ? {
        ...group,
        label: newName.trim(),
        color: type === 'color' ? normalizeHexColor(newColor) : undefined,
        values: selectedValues,
      } : {
        ...group,
        values: group.values.filter(value => !newGroupValues.includes(value.id)),
      }));
      nextUngrouped = [...ungrouped.filter(value => !newGroupValues.includes(value.id)), ...deselectedValues]
        .filter((value, index, values) => values.findIndex(candidate => candidate.id === value.id) === index);
    } else {
      const catalog = new Map([
        ...ungrouped,
        ...groups.reduce((values, group) => [...values, ...group.values], []),
      ].map(value => [value.id, value]));
      const selectedValues = newGroupValues.map(id => catalog.get(id)).filter(Boolean);
      nextGroups = [...groups.map(group => ({
        ...group,
        values: group.values.filter(value => !newGroupValues.includes(value.id)),
      })), {
        id: groupIdFactory(),
        label: newName.trim(),
        color: type === 'color' ? normalizeHexColor(newColor) : undefined,
        values: selectedValues,
      }];
      nextUngrouped = ungrouped.filter(value => !newGroupValues.includes(value.id));
    }
    onChange({ groups: nextGroups, ungrouped: nextUngrouped });
    closeGroupDialog();
  };

  const changeGroupColor = (groupId, color) => {
    onChange({
      groups: groups.map(group => (group.id === groupId
        ? { ...group, color: normalizeHexColor(color) } : group)),
      ungrouped,
    });
  };

  const deleteGroup = () => {
    if (!groupPendingDelete) return;
    const group = groups.find(candidate => candidate.id === groupPendingDelete);
    if (!group) {
      setGroupPendingDelete(null);
      return;
    }
    onChange({
      groups: groups.filter(candidate => candidate.id !== groupPendingDelete),
      ungrouped: [...ungrouped, ...group.values].filter((value, index, values) => (
        values.findIndex(candidate => candidate.id === value.id) === index
      )),
    });
    setGroupPendingDelete(null);
  };

  const openValuePicker = (groupId) => {
    setValueDialogGroupId(groupId);
    setPickerSelection([]);
  };

  const pickerGroup = groups.find(group => group.id === valueDialogGroupId);
  const visibleUngrouped = sortGroupValues(ungrouped.filter(value => (
    !sourceQuery.trim() || value.label.toLowerCase().includes(sourceQuery.trim().toLowerCase())
  )), sourceOrder);
  const groupDialogValues = sortGroupValues([
    ...ungrouped,
    ...groups.reduce((values, group) => [
      ...values,
      ...group.values.filter(value => newGroupValues.includes(value.id)),
    ], []),
  ].filter((value, index, values) => values.findIndex(candidate => candidate.id === value.id) === index), sourceOrder);
  const sortedUngrouped = sortGroupValues(ungrouped, sourceOrder);

  useEffect(() => {
    if (groupDialogDiscard && keepGroupEditingRef.current) keepGroupEditingRef.current.focus();
  }, [groupDialogDiscard]);

  return (
    <DragDropContext
      onBeforeCapture={() => setDraggingGroupValue(true)}
      onDragEnd={handleDrag}
    >
      <div className={`fp-group-workspace ${draggingGroupValue ? 'is-dragging-value' : ''}`}>
        <section className="fp-source-panel">
          <header>
            <div><h3>{t('facets.policy.editor.catalogValues', 'Catalog values')}</h3><p>{t('facets.policy.editor.groups.sourceDescription', 'Drag values into a display group.')}</p></div>
            <strong aria-label={t('facets.policy.editor.groups.ungroupedCount', '{count} ungrouped values', { count: ungrouped.length })}>{ungrouped.length}</strong>
          </header>
          <div className="fp-source-controls">
            <label className="fp-search-field"><SearchIcon /><span className="sr-only">{t('facets.policy.editor.groups.searchCatalogValues', 'Search catalog values')}</span><input value={sourceQuery} onChange={event => setSourceQuery(event.target.value)} placeholder={t('facets.policy.editor.groups.searchValues', 'Search values')} />{sourceQuery && <button type="button" onClick={() => setSourceQuery('')} aria-label={t('facets.policy.editor.groups.clearSearch', 'Clear value search')}>×</button>}</label>
            <div className="fp-value-sort" role="group" aria-label={t('facets.policy.editor.groups.sortValues', 'Sort catalog values')}>
              {[
                ['id', 'ID', t('facets.policy.editor.groups.sortById', 'Sort by ID')],
                ['alpha', t('facets.policy.order.alphaAsc', 'A–Z'), t('facets.policy.editor.groups.sortAlphabetically', 'Sort alphabetically')],
                ['products', t('facets.policy.editor.groups.productsDescending', 'Products ↓'), t('facets.policy.editor.groups.sortByProducts', 'Sort by product count')],
              ].map(([value, label, ariaLabel]) => (
                <button key={value} type="button" className={sourceOrder === value ? 'is-active' : ''} aria-label={ariaLabel} aria-pressed={sourceOrder === value} onClick={() => setSourceOrder(value)}>{label}</button>
              ))}
            </div>
          </div>
          <Droppable droppableId="group-source">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className={`fp-source-list ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                {visibleUngrouped.map((value, index) => (
                  <GroupValue
                    key={value.id}
                    value={value}
                    index={index}
                    source
                    translate={t}
                  />
                ))}
                {provided.placeholder}
                {!ungrouped.length && <div className="fp-complete-state"><CheckIcon /><span>{t('facets.policy.editor.groups.allGrouped', 'Every catalog value is grouped.')}</span></div>}
              </div>
            )}
          </Droppable>
        </section>
        <section className="fp-groups-panel">
          <header>
            <div><h3>{type === 'color' ? t('facets.policy.editor.groups.displayColors', 'Display colors') : t('facets.policy.editor.groups.displayGroups', 'Display groups')}</h3><p>{t('facets.policy.editor.groups.description', 'One storefront choice can contain several catalog values.')}</p></div>
            <button type="button" className="fp-button fp-button--quiet" onClick={() => openNewGroup()}><AddIcon /> {t('facets.policy.editor.groups.add', 'Add group')}</button>
          </header>
          <div className="fp-group-grid">
            {groups.map(group => (
              <article className={`fp-group-card ${type === 'color' ? 'has-swatch' : ''}`} key={group.id} data-group-id={group.id}>
                <header>
                  {type === 'color' && (
                    <label className="fp-group-swatch-control" title={t('facets.policy.editor.groups.changeColor', 'Change color for {label}', { label: group.label })}>
                      <input
                        type="color"
                        aria-label={t('facets.policy.editor.groups.changeColor', 'Change color for {label}', { label: group.label })}
                        value={normalizeHexColor(group.color)}
                        onChange={event => changeGroupColor(group.id, event.target.value)}
                      />
                      <span className="fp-group-swatch" style={{ background: normalizeHexColor(group.color) }} aria-hidden="true" />
                    </label>
                  )}
                  {inlineEditingGroupId === group.id ? (
                    <form className="fp-group-rename-form" onSubmit={saveInlineRename}>
                      <input
                        autoFocus
                        aria-label={t('facets.policy.editor.groups.titleFor', 'Group title for {label}', { label: group.label })}
                        value={inlineGroupName}
                        onFocus={event => event.target.select()}
                        onChange={event => setInlineGroupName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            cancelInlineRename();
                          }
                        }}
                      />
                      <button type="submit" className="fp-icon-button" disabled={!inlineGroupName.trim()} aria-label={t('facets.policy.editor.groups.saveTitle', 'Save title for {label}', { label: group.label })}><CheckIcon /></button>
                      <button type="button" className="fp-icon-button" onClick={cancelInlineRename} aria-label={t('facets.policy.editor.groups.cancelTitle', 'Cancel title editing for {label}', { label: group.label })}><CloseIcon /></button>
                    </form>
                  ) : (
                    <button type="button" className="fp-group-title-button" onClick={() => startInlineRename(group)} aria-label={t('facets.policy.editor.groups.rename', 'Rename {label}', { label: group.label })}>
                      <strong>{group.label}</strong>
                      <small>{group.values.reduce((sum, value) => sum + value.count, 0).toLocaleString()} {t('facets.policy.board.products', 'products')}</small>
                    </button>
                  )}
                  {inlineEditingGroupId !== group.id && (
                    <span className="fp-group-actions">
                      <button type="button" className="fp-icon-button" aria-label={t('facets.policy.editor.addValuesTo', 'Add values to {label}', { label: group.label })} onClick={() => openValuePicker(group.id)}><AddIcon /></button>
                      <button type="button" className="fp-icon-button" aria-label={t('facets.policy.editor.groups.edit', 'Edit {label}', { label: group.label })} onClick={() => openEditGroup(group)}><EditOutlinedIcon /></button>
                      <button type="button" className="fp-icon-button" aria-label={t('facets.policy.editor.groups.delete', 'Delete {label}', { label: group.label })} onClick={() => setGroupPendingDelete(group.id)}><DeleteOutlineIcon /></button>
                    </span>
                  )}
                </header>
                <Droppable droppableId={groupDropId(group.id)}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className={`fp-group-values ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                      {group.values.map((value, index) => <GroupValue key={value.id} value={value} index={index} translate={t} />)}
                      {provided.placeholder}
                      {!group.values.length && <em>{t('facets.policy.editor.dropValuesHere', 'Drop values here')}</em>}
                    </div>
                  )}
                </Droppable>
              </article>
            ))}
            <Droppable droppableId="group-create">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`fp-create-group-drop ${snapshot.isDraggingOver ? 'is-over' : ''}`}
                  data-testid="create-group-drop"
                  aria-hidden={!draggingGroupValue}
                >
                  <AddIcon />
                  <strong>{t('facets.policy.editor.groups.create', 'Create new group')}</strong>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </section>
        {groupDialog && (
          <div className="fp-dialog-backdrop" role="presentation" onMouseDown={requestGroupDialogClose}>
            <div
              className="fp-dialog fp-dialog--group"
              role="dialog"
              aria-modal="true"
              aria-hidden={groupDialogDiscard || undefined}
              aria-labelledby="group-dialog-title"
              onMouseDown={event => event.stopPropagation()}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Escape') requestGroupDialogClose();
                else trapTabInside(event);
              }}
            >
              <form className="fp-dialog-form" onSubmit={(event) => { event.preventDefault(); saveGroup(); }}>
                <header><div><span className="fp-eyebrow">{t('facets.policy.editor.groups.storefrontChoice', 'Storefront choice')}</span><h2 id="group-dialog-title">{editingGroupId ? t('facets.policy.editor.groups.editGroup', 'Edit group') : type === 'color' ? t('facets.policy.editor.groups.addColor', 'Add color') : t('facets.policy.editor.groups.add', 'Add group')}</h2></div><button type="button" className="fp-icon-button" onClick={requestGroupDialogClose} aria-label={t('common.close', 'Close')}><CloseIcon /></button></header>
                <label className="fp-field"><span>{t('facets.policy.editor.groups.name', 'Name')}</span><input autoFocus value={newName} onChange={event => setNewName(event.target.value)} placeholder={type === 'color' ? t('facets.policy.editor.groups.colorExample', 'For example: Warm grey') : t('facets.policy.editor.groups.groupExample', 'For example: Extra large')} /></label>
                {type === 'color' && <label className="fp-field"><span>{t('facets.policy.editor.groups.swatch', 'Swatch')}</span><div className="fp-color-input"><input type="color" value={colorValid ? normalizeHexColor(newColor) : '#8798A5'} onChange={event => setNewColor(event.target.value.toUpperCase())} /><input aria-invalid={!colorValid} value={newColor} onChange={event => setNewColor(event.target.value)} /></div>{!colorValid && <small className="is-error">{t('facets.policy.color.error.hexFormat', 'Use a six-digit hex color, for example #8798A5.')}</small>}</label>}
                <div className="fp-inline-value-picker">
                  <header><strong>{t('facets.policy.editor.section.values', 'Values')}</strong><span>{t('facets.policy.editor.groups.selectedCount', '{count} selected', { count: newGroupValues.length })}</span></header>
                  <ValuePickerList
                    values={groupDialogValues}
                    selection={newGroupValues}
                    onToggle={id => setNewGroupValues(current => (current.includes(id) ? current.filter(candidate => candidate !== id) : [...current, id]))}
                    emptyText={t('facets.policy.editor.groups.allAlreadyGrouped', 'Every catalog value is already grouped.')}
                    translate={t}
                  />
                </div>
                <footer><button type="button" className="fp-button fp-button--quiet" onClick={closeGroupDialog}>{t('common.cancel', 'Cancel')}</button><button type="submit" className="fp-button fp-button--primary" disabled={!newName.trim() || (type === 'color' && !colorValid)}>{editingGroupId ? t('facets.policy.editor.groups.save', 'Save group') : t('facets.policy.editor.groups.add', 'Add group')}</button></footer>
              </form>
            </div>
            {groupDialogDiscard && (
              <div
                className="fp-dialog-backdrop fp-dialog-backdrop--nested"
                role="presentation"
                onMouseDown={(event) => { event.stopPropagation(); setGroupDialogDiscard(false); }}
              >
                <section
                  className="fp-dialog fp-dialog--discard"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="group-discard-dialog-title"
                  onMouseDown={event => event.stopPropagation()}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === 'Escape') setGroupDialogDiscard(false);
                    else trapTabInside(event);
                  }}
                >
                  <header><h2 id="group-discard-dialog-title">{t('facets.policy.editor.discardTitle', 'Discard changes?')}</h2></header>
                  <footer>
                    <button ref={keepGroupEditingRef} type="button" className="fp-button fp-button--quiet" onClick={() => setGroupDialogDiscard(false)}>{t('facets.policy.editor.keepEditing', 'Keep editing')}</button>
                    <button type="button" className="fp-button fp-button--danger" onClick={closeGroupDialog}>{t('common.discard', 'Discard')}</button>
                  </footer>
                </section>
              </div>
            )}
          </div>
        )}
        <ValuePickerDialog
          open={Boolean(pickerGroup)}
          eyebrow={t('facets.policy.editor.catalogValues', 'Catalog values')}
          title={t('facets.policy.editor.addValuesTo', 'Add values to {label}', { label: pickerGroup ? pickerGroup.label : '' })}
          values={sortedUngrouped}
          selection={pickerSelection}
          onToggle={id => setPickerSelection(current => (current.includes(id) ? current.filter(candidate => candidate !== id) : [...current, id]))}
          onClose={() => { setPickerSelection([]); setValueDialogGroupId(null); }}
          onConfirm={() => { moveValues(pickerSelection, groupDropId(pickerGroup.id)); setPickerSelection([]); setValueDialogGroupId(null); }}
          actionLabel={t('facets.policy.editor.addSelected', 'Add selected')}
          translate={t}
        />
        {groupPendingDelete && (
          <div className="fp-dialog-backdrop" role="presentation" onMouseDown={() => setGroupPendingDelete(null)}>
            <section
              className="fp-dialog fp-dialog--discard"
              role="alertdialog"
              aria-modal="true"
              aria-label={t('facets.policy.editor.groups.deleteDisplayGroup', 'Delete display group')}
              onMouseDown={event => event.stopPropagation()}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Escape') setGroupPendingDelete(null);
                else trapTabInside(event);
              }}
            >
              <header><h2>{t('facets.policy.editor.groups.deleteConfirm', 'Delete this group?')}</h2></header>
              <footer>
                <button type="button" className="fp-button fp-button--quiet" onClick={() => setGroupPendingDelete(null)}>{t('common.cancel', 'Cancel')}</button>
                <button type="button" className="fp-button fp-button--danger" onClick={deleteGroup}>{t('facets.policy.editor.groups.deleteGroup', 'Delete group')}</button>
              </footer>
            </section>
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

GroupEditor.propTypes = {
  type: PropTypes.oneOf(['string', 'color']).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    color: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number })),
  })).isRequired,
  ungrouped: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, count: PropTypes.number })).isRequired,
  onChange: PropTypes.func.isRequired,
  groupIdFactory: PropTypes.func,
  translate: PropTypes.func,
};

GroupEditor.defaultProps = {
  groupIdFactory: defaultGroupIdFactory,
  translate: fallbackTranslate,
};

const NumberEditor = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = () => {
    setLoading(true);
    window.setTimeout(() => { setReport(NUMBER_REPORT); setLoading(false); }, 900);
  };
  return (
    <div className="fp-number-editor">
      {!report && !loading && (
        <div className="fp-validation-empty">
          <span><StraightenOutlinedIcon /></span>
          <h3>Check catalog values</h3>
          <p>We will inspect a sample before this facet becomes a number range.</p>
          <button type="button" className="fp-button fp-button--primary" onClick={run}>Check values</button>
        </div>
      )}
      {loading && <div className="fp-validation-empty is-loading"><span className="fp-spinner" /><h3>Checking values…</h3></div>}
      {report && (
        <div>
          <div className="fp-report-cards">
            <article><span>Valid values</span><strong>{report.valid.toLocaleString()}</strong><small>of {report.total.toLocaleString()}</small></article>
            <article className="is-problem"><span>Need attention</span><strong>{report.invalid}</strong><small>products</small></article>
            <article><span>Range</span><strong>€{report.min} – €{report.max.toLocaleString()}</strong><small>detected</small></article>
          </div>
          <div className="fp-invalid-values">
            <header><div><ErrorOutlineIcon /><span><strong>Some values are not numeric</strong><small>Fix them in the catalog, then check again.</small></span></div><button type="button" className="fp-button fp-button--quiet" onClick={run}>Check again</button></header>
            {report.examples.map(example => <div key={example.value || 'empty'}><code>{example.value || 'Empty value'}</code><span>{example.products} products</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
};

const ValuesEditor = ({ type, content, onChange }) => (
  <div className="fp-editor-section fp-editor-section--wide">
    <header className="fp-section-heading">
      <span className="fp-eyebrow">Values</span>
      <h2>{type === 'number' ? 'Validate the range' : 'Turn catalog data into clear choices'}</h2>
    </header>
    {type === 'boolean' && <BooleanMapper values={content.booleanValues} onChange={booleanValues => onChange({ ...content, booleanValues })} />}
    {type === 'color' && <GroupEditor type="color" {...content.groups.color} onChange={color => onChange({ ...content, groups: { ...content.groups, color } })} />}
    {type === 'string' && <GroupEditor type="string" {...content.groups.string} onChange={string => onChange({ ...content, groups: { ...content.groups, string } })} />}
    {type === 'number' && <NumberEditor />}
  </div>
);

ValuesEditor.propTypes = {
  type: PropTypes.string.isRequired,
  content: PropTypes.shape({
    booleanValues: PropTypes.array,
    groups: PropTypes.shape({ string: PropTypes.object, color: PropTypes.object }),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export const DisplayOrderEditor = ({ value, onChange, translate: t }) => {
  const { items, sort } = value;
  const visible = items.filter(item => !item.hidden);
  const pinned = visible.filter(item => item.pinned);
  const automatic = visible.filter(item => !item.pinned);
  const hidden = items.filter(item => item.hidden);

  const togglePin = (id) => {
    onChange({ ...value, items: items.map(item => (item.id === id ? { ...item, pinned: !item.pinned } : item)) });
  };
  const hide = (id) => {
    onChange({ ...value, items: items.map(item => (item.id === id ? { ...item, hidden: true, pinned: false } : item)) });
  };
  const restore = (id) => {
    onChange({ ...value, items: items.map(item => (item.id === id ? { ...item, hidden: false } : item)) });
  };
  const drag = (result) => {
    if (!result.destination) return;
    const id = result.draggableId.replace('display-', '');
    const source = result.source.droppableId;
    const destination = result.destination.droppableId;
    if (destination === 'display-hidden') { hide(id); return; }
    const changed = items.map(item => (item.id === id ? { ...item, hidden: false, pinned: destination === 'display-pinned' } : item));
    let nextItems = changed;
    if (source === destination && destination === 'display-pinned') {
      const fixed = changed.filter(item => item.pinned && !item.hidden);
      const [moved] = fixed.splice(result.source.index, 1);
      fixed.splice(result.destination.index, 0, moved);
      const order = new Map(fixed.map((item, index) => [item.id, index]));
      nextItems = [...changed].sort((a, b) => {
        if (order.has(a.id) && order.has(b.id)) return order.get(a.id) - order.get(b.id);
        if (order.has(a.id)) return -1;
        if (order.has(b.id)) return 1;
        return 0;
      });
    }
    onChange({ ...value, items: nextItems });
  };

  const row = (item, index, zone) => (
    <DraggableCollectionRow key={item.id} draggableId={`display-${item.id}`} index={index} className="fp-display-row fp-direct-row">
      <span className={`fp-order-position ${zone === 'pinned' ? '' : 'is-empty'}`}>{zone === 'pinned' ? index + 1 : ''}</span>
      <span><strong>{item.label}</strong><small>{item.count.toLocaleString()} {t('facets.policy.board.products', 'products')}</small></span>
      {zone !== 'hidden'
        ? <PinAction pinned={item.pinned} label={item.pinned ? t('facets.policy.board.unpin', 'Unpin {title}', { title: item.label }) : t('facets.policy.board.pin', 'Pin {title}', { title: item.label })} onClick={() => togglePin(item.id)} />
        : <span className="fp-action-spacer" aria-hidden="true" />}
      <button type="button" className="fp-icon-button" aria-label={zone === 'hidden' ? t('facets.policy.board.restore', 'Restore {title}', { title: item.label }) : t('facets.policy.board.hide', 'Hide {title}', { title: item.label })} onClick={() => (zone === 'hidden' ? restore(item.id) : hide(item.id))}>{zone === 'hidden' ? <CheckIcon /> : <VisibilityOffOutlinedIcon />}</button>
    </DraggableCollectionRow>
  );

  return (
    <div className="fp-editor-section fp-editor-section--display">
      <header className="fp-section-heading"><span className="fp-eyebrow">{t('facets.policy.editor.displayOrder', 'Display order')}</span><h2>{t('facets.policy.editor.displayOrderHeading', 'Choose what shoppers notice first')}</h2></header>
      <DragDropContext onDragEnd={drag}>
        <div className="fp-display-workspace">
          <section className="fp-pinned-values">
            <header><div><h3>{t('facets.policy.editor.display.pinnedFirst', 'Pinned first')}</h3><p>{t('facets.policy.editor.display.pinnedDescription', 'Drag in the exact order you want.')}</p></div><strong>{pinned.length}</strong></header>
            <Droppable droppableId="display-pinned">
              {(provided, snapshot) => <div ref={provided.innerRef} {...provided.droppableProps} className={`fp-display-list ${snapshot.isDraggingOver ? 'is-over' : ''}`}>{pinned.map((item, index) => row(item, index, 'pinned'))}{provided.placeholder}{!pinned.length && <span className="fp-drop-empty">{t('facets.policy.editor.display.dropPinned', 'Drop values to pin them')}</span>}</div>}
            </Droppable>
          </section>
          <section className="fp-automatic-values">
            <header><div><h3>{t('facets.policy.board.zone.automatic.title', 'Automatic order')}</h3><p>{t('facets.policy.editor.display.automaticDescription', 'Sorted by the selected default.')}</p></div><div className="fp-compact-select"><button type="button">{sort === 'count-desc' ? t('facets.policy.order.countDesc', 'Most products') : t('facets.policy.order.alphaAsc', 'A–Z')} <ExpandMoreIcon /></button><div><button type="button" onClick={() => onChange({ ...value, sort: 'count-desc' })}>{t('facets.policy.order.countDesc', 'Most products')}</button><button type="button" onClick={() => onChange({ ...value, sort: 'az' })}>{t('facets.policy.order.alphaAsc', 'A–Z')}</button></div></div></header>
            <Droppable droppableId="display-auto">
              {(provided, snapshot) => <div ref={provided.innerRef} {...provided.droppableProps} className={`fp-display-list ${snapshot.isDraggingOver ? 'is-over' : ''}`}>{automatic.map((item, index) => row(item, index, 'auto'))}{provided.placeholder}</div>}
            </Droppable>
          </section>
          <section className="fp-hidden-values">
            <header><div><h3>{t('facets.policy.hidden', 'Hidden')}</h3><p>{t('facets.policy.editor.display.hiddenDescription', 'These choices never appear in the storefront.')}</p></div><strong>{hidden.length}</strong></header>
            <Droppable droppableId="display-hidden">
              {(provided, snapshot) => <div ref={provided.innerRef} {...provided.droppableProps} className={`fp-display-list ${snapshot.isDraggingOver ? 'is-over' : ''}`}>{hidden.map((item, index) => row(item, index, 'hidden'))}{provided.placeholder}{!hidden.length && <span className="fp-drop-empty">{t('facets.policy.editor.display.dropHidden', 'Drop values to hide them')}</span>}</div>}
            </Droppable>
          </section>
        </div>
      </DragDropContext>
    </div>
  );
};

DisplayOrderEditor.propTypes = {
  value: PropTypes.shape({ items: PropTypes.array, sort: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  translate: PropTypes.func,
};

DisplayOrderEditor.defaultProps = { translate: fallbackTranslate };

const FacetEditorPrototype = ({ facet, savedConfig, onSave, onClose }) => {
  const [tab, setTab] = useState('basics');
  const initialConfigRef = useRef(savedConfig ? cloneFixture(savedConfig) : {
    draft: {
      title: facet.title,
      type: facet.type,
      visible: true,
      searchable: facet.type === 'string',
    },
    content: createInitialContent(),
  });
  const baselineRef = useRef(JSON.stringify(initialConfigRef.current));
  const [draft, setDraft] = useState(initialConfigRef.current.draft);
  const [content, setContent] = useState(initialConfigRef.current.content);
  const [discardDialog, setDiscardDialog] = useState(false);
  const editorRef = useRef(null);
  const closeRef = useRef(null);
  const keepEditingRef = useRef(null);
  const dirtyRef = useRef(false);
  const dirty = JSON.stringify({ draft, content }) !== baselineRef.current;
  dirtyRef.current = dirty;

  useEffect(() => {
    if (draft.type !== 'string' && tab === 'display') setTab('values');
  }, [draft.type, tab]);

  useEffect(() => {
    document.body.classList.add('fp-editor-open');
    if (closeRef.current) closeRef.current.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (event.target.closest('.fp-dialog, .fp-group-rename-form')) return;
        if (dirtyRef.current) setDiscardDialog(true);
        else onClose();
        return;
      }
      if (event.key !== 'Tab' || !editorRef.current) return;
      const focusable = Array.from(editorRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )).filter(element => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.body.classList.remove('fp-editor-open'); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  useEffect(() => {
    if (discardDialog && keepEditingRef.current) keepEditingRef.current.focus();
  }, [discardDialog]);

  const requestClose = () => {
    if (dirty) setDiscardDialog(true);
    else onClose();
  };

  const save = () => {
    if (!draft.title.trim()) { setTab('basics'); return; }
    onSave(cloneFixture({ draft, content }));
    onClose();
  };

  return (
    <div className="fp-editor-backdrop" role="presentation" onMouseDown={requestClose}>
      <section ref={editorRef} className="fp-full-editor" role="dialog" aria-modal="true" aria-hidden={discardDialog || undefined} aria-labelledby="facet-editor-title" onMouseDown={event => event.stopPropagation()}>
        <header className="fp-editor-topbar">
          <div className="fp-editor-identity"><span className={`fp-type-icon fp-type-icon--${draft.type}`}><TypeIcon type={draft.type} /></span><div><span className="fp-eyebrow">Filter</span><h1 id="facet-editor-title">{draft.title || 'Untitled filter'}</h1></div></div>
          <div className="fp-editor-actions">
            <button type="button" className="fp-button fp-button--primary" disabled={!dirty || !draft.title.trim()} onClick={save}>Save changes</button>
            <button ref={closeRef} type="button" className="fp-icon-button fp-editor-close" aria-label="Close filter editor" onClick={requestClose}><CloseIcon /></button>
          </div>
        </header>
        <nav className="fp-editor-tabs" aria-label="Filter editor sections">
          {[
            ['basics', 'Basics'],
            ['values', draft.type === 'number' ? 'Data check' : 'Values'],
            ...(draft.type === 'string' ? [['display', 'Display order']] : []),
          ].map(([id, label]) => <button type="button" key={id} className={tab === id ? 'is-active' : ''} aria-current={tab === id ? 'page' : undefined} onClick={() => setTab(id)}>{label}</button>)}
        </nav>
        <div className="fp-editor-body">
          <main>
            {tab === 'basics' && <BasicsEditor draft={draft} setDraft={setDraft} onContinue={() => setTab('values')} />}
            {tab === 'values' && <ValuesEditor type={draft.type} content={content} onChange={setContent} />}
            {tab === 'display' && <DisplayOrderEditor value={content.displayOrder} onChange={displayOrder => setContent(current => ({ ...current, displayOrder }))} />}
          </main>
        </div>
      </section>
      {discardDialog && (
        <div
          className="fp-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => { event.stopPropagation(); setDiscardDialog(false); }}
        >
          <section
            className="fp-dialog fp-dialog--discard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="discard-dialog-title"
            onMouseDown={event => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Escape') setDiscardDialog(false);
              else trapTabInside(event);
            }}
          >
            <header><h2 id="discard-dialog-title">Discard changes?</h2></header>
            <footer>
              <button ref={keepEditingRef} type="button" className="fp-button fp-button--quiet" onClick={() => setDiscardDialog(false)}>Keep editing</button>
              <button type="button" className="fp-button fp-button--danger" onClick={onClose}>Discard</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

FacetEditorPrototype.propTypes = {
  facet: PropTypes.shape({ title: PropTypes.string, type: PropTypes.string }).isRequired,
  savedConfig: PropTypes.shape({ draft: PropTypes.object, content: PropTypes.object }),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

FacetEditorPrototype.defaultProps = { savedConfig: null };

export default FacetEditorPrototype;
